import * as Sentry from '@sentry/vue'
import type { Event, EventHint } from '@sentry/types'
import { DENY_URLS, IGNORE_ERRORS, RELEASE_IN_PROGRESS_SENTRY_EVENT_TYPE } from '@/utils/constants/sentry'
import { addBrowserContext, beforeSend } from '@/utils/sentry'
const isTestEnv = process.env.TEST === 'true' || process.env.NODE_ENV === 'test' || process.env.VITEST === 'true'

export const handleClientBeforeSend = (
  event: Event,
  hint: EventHint,
  routeName?: string | symbol | null
): ReturnType<typeof beforeSend> => {
  addBrowserContext(event, routeName)

  if (event.tags?.event_type === RELEASE_IN_PROGRESS_SENTRY_EVENT_TYPE) return event

  return beforeSend(event, hint)
}

function getSentryIntegrations() {
  if (!import.meta.client) return []

  const router = useRouter()
  const browserTracing = new Sentry.BrowserTracing({
    routingInstrumentation: Sentry.vueRouterInstrumentation(router)
  })

  return [browserTracing]
}

export default defineNuxtPlugin({
  name: 'sentry',
  parallel: true,
  async setup(nuxtApp) {
    if (isTestEnv) return

    const vueApp = nuxtApp.vueApp

    const config = useRuntimeConfig()
    const route = useRoute()
    const isLocal = config.public.env === 'local'
    const isDev = config.public.env !== 'production'

    if (!config.public.sentryDsn || isLocal) {
      console.warn('Sentry DSN not set, skipping Sentry initialization')
      return
    }

    Sentry.init({
      app: vueApp,
      dsn: config.public.sentryDsn,
      debug: isDev,
      environment: config.public.sentryEnv,
      integrations: getSentryIntegrations(),
      tracePropagationTargets: [/^\/api\//],
      tracesSampleRate: Number.parseFloat(config.public.sentrySampleRate),
      maxBreadcrumbs: 100,
      normalizeDepth: 5,
      attachStacktrace: true,
      autoSessionTracking: true,
      ignoreErrors: IGNORE_ERRORS,
      denyUrls: DENY_URLS,
      beforeSend: (event: Event, hint: EventHint) => handleClientBeforeSend(event, hint, route.name)
    })

    Sentry.setTags({
      app_env: config.public.env,
      sentry_env: config.public.sentryEnv
    })
  }
})
